import Constants from 'expo-constants';

import type { ApplePayContext, PipelineStepContext, PipelineStepResult } from '..';
import { PaymentProcessor, PipelineBaseStep } from '..';

interface IWorldpayConfig {
  eProtectUrl: string;
  paypageId: string;
}

interface IEProtectPayPageResponse {
  paypageRegistrationId: string;
  type: string;
  bin: string;
  lastFour: string;
  expMonth?: string;
  expYear?: string;
  message?: string;
  response?: string;
}

/**
 * Converts Apple Pay token for Worldpay
 *
 * For Worldpay, Apple Pay returns encrypted payment data that needs to be
 * exchanged for a low value token via the eProtect API.
 *
 * This step:
 * 1. Extracts Apple Pay payment details
 * 2. Calls Worldpay eProtect API to exchange for low value token
 * 3. Updates the context with the low value token
 *
 * Worldpay expects:
 * - applePayDetails.paymentData = Worldpay low value token
 * - Other fields remain for backend processing
 */
export class WorldpayApplePayConversionStep extends PipelineBaseStep {
  readonly name = 'WorldpayApplePayConversionStep';
  private region: 'US' | 'CA';

  constructor() {
    super();
    this.region = Constants?.expoConfig?.extra?.eas?.buildRegion;
  }

  async execute(context: PipelineStepContext): Promise<PipelineStepResult> {
    // Only run for Apple Pay with Worldpay
    if (context.paymentMethod !== 'applePay' || context.processor !== PaymentProcessor.WORLDPAY) {
      return this.success();
    }

    const applePayContext = context as ApplePayContext;
    const { applePayDetails } = applePayContext;

    this.logger.log('[ApplePay] Converting token for Worldpay', {
      processor: context.processor,
      hasPaymentData: !!applePayDetails.paymentData,
    });

    try {
      // Get Worldpay configuration
      const worldpayConfig = this.getWorldpayConfig();

      // Request low value token from Worldpay eProtect
      const lowValueToken = await this.requestWorldpayLowValueToken(
        applePayDetails,
        worldpayConfig,
        context.rbiOrderId
      );

      // Update the payment data with the low value token
      const convertedDetails = {
        ...applePayDetails,
        paymentData: lowValueToken,
      };

      this.logger.log('[ApplePay] Worldpay token conversion complete', {
        tokenLength: lowValueToken.length,
        paymentType: convertedDetails.PaymentMethodData.paymentType,
      });

      // Return updated context with converted details
      return this.success({
        applePayDetails: convertedDetails,
      } as Partial<ApplePayContext>);
    } catch (error) {
      this.logger.logError('[ApplePay] Worldpay token conversion failed', error);

      return this.error(
        'WORLDPAY_APPLE_PAY_CONVERSION_FAILED',
        error instanceof Error ? error.message : 'Unknown error',
        'Failed to process Apple Pay payment. Please try again. Error Code: (Worldpay Conversion Failed)',
        'WorldpayApplePayConversionStep'
      );
    }
  }

  private getWorldpayConfig(): IWorldpayConfig {
    const worldpayConfig =
      Constants?.expoConfig?.extra?.eas?.US?.worldpay ||
      Constants?.expoConfig?.extra?.eas?.CA?.worldpay;

    if (!worldpayConfig?.eProtectUrl || !worldpayConfig?.paypageId) {
      throw new Error('Worldpay configuration not found or incomplete');
    }

    return {
      eProtectUrl: worldpayConfig.eProtectUrl,
      paypageId: worldpayConfig.paypageId,
    };
  }

  private async requestWorldpayLowValueToken(
    applePayDetails: any,
    worldpayConfig: IWorldpayConfig,
    rbiOrderId: string
  ): Promise<string> {
    const merchantOrderId = rbiOrderId;
    const env = Constants?.expoConfig?.extra?.env;
    const merchantReportGroup = `${env}-FHS-${this.region}`.toUpperCase();

    this.logger.log('[ApplePay] Requesting Worldpay low value token', {
      merchantOrderId,
      merchantReportGroup,
    });

    const data = {
      paypageId: worldpayConfig.paypageId,
      reportGroup: merchantReportGroup,
      orderId: merchantOrderId,
      id: merchantOrderId,
      accountNumber: 'N/A',
      'applepay.data': applePayDetails.data,
      'applepay.signature': applePayDetails.signature,
      'applepay.version': applePayDetails.version,
      'applepay.header.applicationData': applePayDetails.applicationData || '',
      'applepay.header.ephemeralPublicKey': applePayDetails.ephemeralPublicKey,
      'applepay.header.publicKeyHash': applePayDetails.publicKeyHash,
      'applepay.header.transactionId': applePayDetails.transactionId,
    };

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    };

    const paypageUrl = `${worldpayConfig.eProtectUrl}/eProtect/paypage`;

    this.logger.log('[ApplePay] Calling Worldpay eProtect API', { url: paypageUrl });

    const response = await fetch(paypageUrl, {
      method: 'POST',
      headers,
      body: this.encodeFormData(data),
    });

    if (!response.ok) {
      throw new Error(`Worldpay eProtect API returned ${response.status}: ${response.statusText}`);
    }

    const text = await response.text();
    let resData: IEProtectPayPageResponse;

    try {
      resData = JSON.parse(text);
    } catch (error) {
      this.logger.log('[ApplePay] Worldpay returned invalid JSON response', {
        status: response.status,
        text,
        error,
      });
      throw new Error('Invalid JSON response from Worldpay eProtect');
    }

    if (!resData.paypageRegistrationId) {
      const errorMessage = resData.message || resData.response || 'Unknown error from Worldpay';
      this.logger.logError('[ApplePay] Worldpay eProtect did not return registration ID', {
        response: resData,
      });
      throw new Error(`Worldpay eProtect error: ${errorMessage}`);
    }

    this.logger.log('[ApplePay] Successfully received Worldpay low value token', {
      tokenLength: resData.paypageRegistrationId.length,
    });

    return resData.paypageRegistrationId;
  }

  private encodeFormData(data: Record<string, string>): string {
    const formBody: string[] = [];
    for (const property in data) {
      const encodedKey = encodeURIComponent(property);
      const encodedValue = encodeURIComponent(data[property]);
      formBody.push(`${encodedKey}=${encodedValue}`);
    }
    return formBody.join('&');
  }
}
